import { createSlice } from '@reduxjs/toolkit';
import { restorationFormQuestion } from '../../pages/SmileDesign/RestorativePhaseButton/RestorationFormQuestion';
import { set } from 'date-fns';

export const smileDesignProductList = {
    full: {
        name: 'Full Smile Design',
        type: 'FULL_SMILE_DESIGN',
    },
    simple: {
        name: 'Simple Smile Design',
        type: 'SIMPLE_SMILE_DESIGN',
    },
};

export const smileDesignProductListArray = Object.values(smileDesignProductList).map((product) => product.type);

function checkSmileDesignExistence(productMatrixList) {
    return productMatrixList.some(
        (product) =>
            product.case_type === smileDesignProductList.full.type ||
            product.case_type === smileDesignProductList.simple.type
    );
}

/**
 * Redux slice for managing smile design state.
 * @type {import("@reduxjs/toolkit").Slice}
 */
export const smileDesignSlice = createSlice({
    name: 'smileDesign',
    initialState: {
        isSmileDesignCase: false,
        productMatrixArray: [],
        smileDesignProductList: smileDesignProductList,
        smileDesignProductListArray: smileDesignProductListArray, // Purpose: hardcode in smileDesignProductList to obtain array
        caseType: '',
        caseId: '',

        smileDesignPrescriptionFormData: {},
        handleValidateSmileDesignPrescriptionNotes: () => {},
        getSmileDesignPrescriptionFormValues: () => ({}),
        smileDesignPrescriptionFormIsValid: false,
        applyQ14DefaultsToISDForm: () => {},
        validateISDQ4Q14Match: () => true,

        restorationFormData: {},
        handleValidateRestorationFormData: () => {},
        restorationFormIsValid: false,
        // restorationImpressionData: {},
        // handleValidateRestorationImpressionData: () => { }, // Purpose: Pass the handlDataSave to RestorationForm.js to trigger validation in newPatientReducer

        isIDesign: true, // Purpose: toggle Button in clinicalSmulation page to hide and show iDesign
        setIsIDesign: () => {},

        isExocad: true, // Purpose: toggle Button in clinicalSmulation page to hide and show isExocad
        setIsExocad: () => {},

        isMaximizeIDesign: false, // Purpose: toggle Button in clinicalSmulation page to maximize iDesign
        setIsMaximizeIDesign: () => {},

        // Purpose: Click approve treatment plan to show the modal for idesign approvement or smile design approvement. Control Modal
        isClinicalSmulationApproveTreatmentPlan: false,
        setIsClinicalSmulationApproveTreatmentPlan: () => {},
        clinicalApprovementTreatmentPlanData: '', // Purpose: Ans of the clinical approvement treatment plan
        setClinicalApprovementTreatmentPlanData: () => {},
        planId: null,
        //

        isReleaseSmileDesignByDistriAndAllowUserTest: false,
        isReleaseSmileDesignByDistri: false,

        refreshApiByUpdateRequest: false,
    },

    reducers: {
        setIsSmileDesignCase: (state, action) => {
            state.isSmileDesignCase = action.payload;
        },
        setProductMatrixArray: (state, action) => {
            state.productMatrixArray = action.payload;
        },
        setCaseType: (state, action) => {
            state.caseType = action.payload;
        },

        setCaseId: (state, action) => {
            state.caseId = action.payload;
        },

        // Smile Design Prescription
        setSmileDesignPrescriptionFormData: (state, action) => {
            state.smileDesignPrescriptionFormData = action.payload;
        },
        setHandleValidateSmileDesignPrescriptionNotes: (state, action) => {
            state.handleValidateSmileDesignPrescriptionNotes = action.payload;
        },
        setGetSmileDesignPrescriptionFormValues: (state, action) => {
            state.getSmileDesignPrescriptionFormValues = action.payload;
        },
        setSmileDesignPrescriptionFormIsValid: (state, action) => {
            state.smileDesignPrescriptionFormIsValid = action.payload;
        },
        setApplyQ14DefaultsToISDForm: (state, action) => {
            state.applyQ14DefaultsToISDForm = action.payload;
        },
        setValidateISDQ4Q14Match: (state, action) => {
            state.validateISDQ4Q14Match = action.payload;
        },

        setIsIDesign: (state, action) => {
            state.isIDesign = action.payload;
        },
        setIsExocad: (state, action) => {
            state.isExocad = action.payload;
        },

        setIsMaximizeIDesign: (state, action) => {
            state.isMaximizeIDesign = action.payload;
        },

        setIsClinicalSmulationApproveTreatmentPlan: (state, action) => {
            state.isClinicalSmulationApproveTreatmentPlan = action.payload;
        },
        setClinicalApprovementTreatmentPlanData: (state, action) => {
            state.clinicalApprovementTreatmentPlanData = action.payload;
        },
        setPlanId: (state, action) => {
            state.planId = action.payload;
        },

        /**
         * Reducer for checking and setting the state based on product matrix list and feature list.
         * @param {Object} state - The current state.
         * @param {Object} action - The action containing payload with productMatrixList and featureList.
         */

        checkReleaseSmileDesignByDstriAndAllowUserTest: (state, action) => {
            const { productMatrixList, featureList } = action.payload;
            state.isReleaseSmileDesignByDistriAndAllowUserTest =
                checkSmileDesignExistence(productMatrixList) && featureList?.['smile_design'];
        },

        /**
         * Reducer for checking and setting the state based on product matrix list only.
         * @param {Object} state - The current state.
         * @param {Object} action - The action containing payload with productMatrixList.
         */

        checkReleaseSmileDesignByDstri: (state, action) => {
            const { productMatrixList } = action.payload;
            state.isReleaseSmileDesignByDistri = checkSmileDesignExistence(productMatrixList);
        },

        setRefreshApiByUpdateRequest: (state, action) => {
            state.refreshApiByUpdateRequest = action.payload;
        },
    },
});

export const {
    setIsSmileDesignCase,
    setProductMatrixArray,
    setCaseType,
    setCaseId,

    setSmileDesignPrescriptionFormData,
    setHandleValidateSmileDesignPrescriptionNotes,
    setGetSmileDesignPrescriptionFormValues,
    setSmileDesignPrescriptionFormIsValid,
    setApplyQ14DefaultsToISDForm,
    setValidateISDQ4Q14Match,
    setIsIDesign,
    setIsExocad,
    setIsMaximizeIDesign,

    setIsClinicalSmulationApproveTreatmentPlan,
    setClinicalApprovementTreatmentPlanData,
    setPlanId,
    checkReleaseSmileDesignByDstriAndAllowUserTest,
    checkReleaseSmileDesignByDstri,
    setRefreshApiByUpdateRequest,
} = smileDesignSlice.actions;

export default smileDesignSlice.reducer;
